import { useState, useMemo, useRef, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowRight, Film, Trash2, LayoutGrid, Image, RefreshCw, Copy, MoreVertical, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'
import { formatFileSize } from '@/lib/utils'
import { VideoPreview } from '@/components/VideoPreview'
import { useVideoWithAudit } from '@/features/videos/use-video-audit'
import { framesStorageService } from '@/features/frames/frames-store'
import { useCloudVideos, useCloudStorage } from '@/features/videos/use-cloud-videos'
import { Progress } from '@/components/ui/progress'
import { 
  VideoLibrarySortFilter, 
  VideoFilters
} from '@/components/VideoLibrarySortFilter'
import { sortVideos, filterVideos } from '@/lib/video-library-utils'
import { usePullToRefresh } from '@/hooks/use-pull-to-refresh'
import { PullToRefreshIndicator } from '@/components/PullToRefreshIndicator'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface LibraryPageProps {
  embedded?: boolean
}

export function LibraryPage({ embedded = false }: LibraryPageProps) {
  const { videos, isLoading, error, deleteVideo, duplicateVideo, refresh } = useCloudVideos()
  const { storageInfo } = useCloudStorage()
  const { logVideoAction } = useVideoWithAudit()
  const [duplicatingVideoId, setDuplicatingVideoId] = useState<string | null>(null)
  
  // Pull-to-refresh setup
  const containerRef = useRef<HTMLDivElement>(null)
  const handleRefresh = useCallback(async () => {
    await refresh()
  }, [refresh])
  
  const { pullDistance, isRefreshing } = usePullToRefresh(containerRef, {
    onRefresh: handleRefresh,
    enabled: !isLoading,
  })
  
  // Filters state
  const [filters, setFilters] = useState<VideoFilters>({
    search: '',
    sortField: 'date',
    sortOrder: 'desc',
    formats: [],
    viewMode: 'grid',
  })
  
  // Get available formats from videos
  const availableFormats = useMemo(() => {
    const formats = new Set<string>()
    videos.forEach((video) => {
      if (video.mimeType) {
        const format = video.mimeType.split('/')[1]?.toLowerCase()
        if (format) formats.add(format)
      }
    })
    return Array.from(formats).sort()
  }, [videos])
  
  // Filter and sort videos
  const filteredVideos = useMemo(() => {
    const filtered = filterVideos(videos, filters)
    return sortVideos(filtered, filters.sortField, filters.sortOrder)
  }, [videos, filters])

  // Handle video removal
  const handleRemoveVideo = async (videoId: string, videoName: string) => {
    if (confirm(`Are you sure you want to remove "${videoName}" from your local library?`)) {
      const deleteToast = toast.loading(`Deleting "${videoName}"...`)
      
      try {
        await deleteVideo(videoId)
        
        // Also delete any associated frames
        try {
          await framesStorageService.deleteFramesByVideo(videoId)
        } catch {
          // Frames may not exist, that's okay
        }
        
        // Log video deletion to audit log
        logVideoAction('video_deleted', `Video removed: ${videoName}`, {
          videoId,
          videoName
        }, { toast: false })
        
        toast.success(`Removed "${videoName}" from your library`, { id: deleteToast })
      } catch (error) {
        console.error('Failed to delete video:', error)
        toast.error('Failed to remove video', { 
          id: deleteToast,
          description: error instanceof Error ? error.message : 'Please try again'
        })
      }
    }
  }

  // Handle video duplication
  const handleDuplicateVideo = async (videoId: string, videoName: string) => {
    if (duplicatingVideoId) return

    const duplicateToast = toast.loading(`Duplicating "${videoName}"...`)
    setDuplicatingVideoId(videoId)

    try {
      const duplicatedVideo = await duplicateVideo(videoId)

      logVideoAction('video_duplicated', `Video duplicated: ${duplicatedVideo.name}`, {
        videoId: duplicatedVideo.id,
        videoName: duplicatedVideo.name,
      }, {
        toast: false,
        additionalDetails: { sourceVideoId: videoId, sourceVideoName: videoName }
      })

      toast.success(`Created "${duplicatedVideo.name}"`, { id: duplicateToast })
    } catch (error) {
      console.error('Failed to duplicate video:', error)
      toast.error('Failed to duplicate video', {
        id: duplicateToast,
        description: error instanceof Error ? error.message : 'Please try again'
      })
    } finally {
      setDuplicatingVideoId(null)
    }
  }

  // Format storage size
  const formatSize = (bytes: number) => {
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let i = 0
    let size = bytes
    while (size >= 1024 && i < units.length - 1) { size /= 1024; i++ }
    return `${size.toFixed(1)} ${units[i]}`
  }

  return (
    <div 
      ref={containerRef}
      className={embedded ? '' : 'container mx-auto px-4 py-6 md:py-8 pb-20 md:pb-8 overflow-auto'}
    >
      {/* Pull-to-refresh indicator - mobile only */}
      <PullToRefreshIndicator 
        pullDistance={pullDistance} 
        isRefreshing={isRefreshing} 
      />
      
      {!embedded && (
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">My Video Library</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Videos are saved in this browser for your next visit
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/?view=library&library=frames">
              <Image className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Frame Captures</span>
              <span className="sm:hidden">Frames</span>
            </Link>
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => refresh()}
            disabled={isLoading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button size="sm" asChild>
            <Link to="/?view=studio">
              <span className="hidden sm:inline">Upload New Video</span>
              <span className="sm:hidden">Upload</span>
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
      )}
      
      {/* Storage information display */}
      {!embedded && (
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Local Storage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Used: {formatSize(storageInfo.used)}</span>
              <span>Total: {formatSize(storageInfo.quota)}</span>
            </div>
            <Progress value={storageInfo.percentUsed} className="h-2" />
            <p className="text-sm text-muted-foreground">
              {storageInfo.percentUsed}% used • {storageInfo.videoCount} video{storageInfo.videoCount !== 1 ? 's' : ''} saved locally
            </p>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Error display */}
      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => refresh()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Sort and filter controls */}
      {!isLoading && videos.length > 0 && (
        <div className="mb-6">
          <VideoLibrarySortFilter
            filters={filters}
            onFiltersChange={setFilters}
            availableFormats={availableFormats}
            totalCount={videos.length}
            filteredCount={filteredVideos.length}
          />
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <div className="aspect-video bg-muted">
                <Skeleton className="w-full h-full" />
              </div>
              <CardHeader className="pb-2">
                <Skeleton className="h-6 w-4/5 mb-2" />
                <Skeleton className="h-4 w-3/5" />
              </CardHeader>
              <CardContent>
                <div className="flex justify-between">
                  <Skeleton className="h-9 w-32" />
                  <Skeleton className="h-9 w-24" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredVideos.length > 0 ? (
        <div className={
          filters.viewMode === 'grid' 
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : "flex flex-col gap-4"
        }>
          {filteredVideos.map((video) => (
            <Card key={video.id} className="overflow-hidden transition-all hover:shadow-md">
              <div className="aspect-video bg-muted relative">
                {video.url ? (
                  <VideoPreview
                    videoSrc={video.url}
                    videoName={video.name}
                    videoId={video.id}
                    thumbnailUrl={video.thumbnailUrl}
                    className="w-full h-full"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Film size={64} className="text-muted-foreground" />
                  </div>
                )}
                
                {/* Mime type badge */}
                {video.mimeType && (
                  <div className="absolute top-2 left-2 bg-background/80 backdrop-blur-sm text-foreground text-xs px-2 py-1 rounded">
                    {video.mimeType.split('/')[1]?.toUpperCase() || 'VIDEO'}
                  </div>
                )}
                
                {/* Duration badge */}
                {video.duration && (
                  <div className="absolute bottom-2 right-2 bg-background/80 backdrop-blur-sm text-foreground text-xs px-2 py-1 rounded">
                    {video.duration}
                  </div>
                )}
              </div>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-lg line-clamp-1">{video.name}</CardTitle>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                        <MoreVertical className="h-4 w-4" />
                        <span className="sr-only">Open video actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={`/?view=studio&video=${video.id}`}>
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Open
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={duplicatingVideoId === video.id}
                        onClick={() => handleDuplicateVideo(video.id, video.name)}
                      >
                        <Copy className={`mr-2 h-4 w-4 ${duplicatingVideoId === video.id ? 'animate-pulse' : ''}`} />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => handleRemoveVideo(video.id, video.name)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <CardDescription className="mt-1">
                  <div className="flex items-center text-xs space-x-2">
                    <span>{new Date(video.uploadDate).toLocaleDateString()}</span>
                    <span>•</span>
                    {video.frameCount ? (
                      <>
                        <span className="flex items-center">
                          <LayoutGrid size={12} className="mr-1" />
                          {video.frameCount.toLocaleString()} frames
                        </span>
                        <span>•</span>
                      </>
                    ) : null}
                    <span>{formatFileSize(video.size)}</span>
                  </div>
                </CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : videos.length > 0 ? (
        // Has videos but filters return nothing
        <Card className="w-full py-16">
          <CardContent className="flex flex-col items-center justify-center text-center">
            <Film size={64} className="text-muted-foreground mb-4" />
            <h2 className="text-2xl font-semibold mb-2">No videos match your filters</h2>
            <p className="text-muted-foreground mb-6">
              Try adjusting your search or filter criteria.
            </p>
            <Button 
              variant="outline"
              onClick={() => setFilters({
                search: '',
                sortField: 'date',
                sortOrder: 'desc',
                formats: [],
                viewMode: filters.viewMode,
              })}
            >
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="w-full py-16">
          <CardContent className="flex flex-col items-center justify-center text-center">
            <Film size={64} className="text-muted-foreground mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Your library is empty</h2>
            <p className="text-muted-foreground mb-6 max-w-md">
              You haven't uploaded any videos yet. Start by uploading a video to extract frames from.
              Videos are stored locally in this browser.
            </p>
            <Button asChild>
              <Link to="/?view=studio">
                Upload Your First Video
                <ArrowRight className="ml-2" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
